import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import * as Sentry from "@sentry/react"
import api from '../api/client'
import LoadingScreen from '../components/LoadingScreen'
import Card from '../components/Card'
import Button from '../components/Button'
import Tag from '../components/Tag'
import Table from '../components/Table'
import TextField from '../components/TextField'
import Pagination from '../components/Pagination'
import Modal from '../components/Modal'
import DownloadSettings from '../components/DownloadSettings'
import './styles.css'

// Importar iconos
import iconDownload from '../assets/icons/download.svg'
import logsIcon from '../assets/icons/logs-icon.png'

export default function AgenteDetalle() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [agente, setAgente] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sortedColumn, setSortedColumn] = useState(null)
  const [searchText, setSearchText] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false)
  const [fileType, setFileType] = useState('pdf')

  useEffect(() => {
    fetchAgente()
  }, [id])

  const fetchAgente = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/agentes/${id}`)
      setAgente(response.data)
    } catch (err) {
      Sentry.captureException(new Error('Error loading agente: ' + err.message))
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleBack = () => {
    navigate('/logs')
  }

  if (loading) return <LoadingScreen />
  if (error) return <div className="error-message">Error: {error}</div>
  if (!agente) return <div>No se encontró el agente</div>

  const data = agente

  const handleSort = (columnKey) => {
    if (sortedColumn === columnKey) {
      setSortedColumn(null)
    } else {
      setSortedColumn(columnKey)
    }
  }

  return (
    <div className="page-especie-detalle">
      <div className="page-header-detalle">
        <div className="page-title-section">
          <h1 className="page-title-detalle">Agente {data.numero}</h1>
          <div className="breadcrumbs">
            <button className="breadcrumb-link" onClick={handleBack}>Logs</button>
            <span className="breadcrumb-separator"> &gt; </span>
            <span className="breadcrumb-current">Agente {data.numero}</span>
          </div>
        </div>
      </div>

      <div className="page-content-detalle">
        <Card>
          <div className="card-content">
            <h3 className="card-title-detalle">Datos personales</h3>
            <div className="capture-details">
              <div className="detail-row">
                <span className="detail-label">Nombre</span>
                <span className="detail-value">{data.nombre}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Apellidos</span>
                <span className="detail-value">{data.apellidos}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">DNI</span>
                <span className="detail-value">{data.dni}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">E-mail</span>
                <span className="detail-value">{data.email}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Teléfono móvil</span>
                <span className="detail-value">{data.telefono}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Municipio</span>
                <span className="detail-value">{data.municipio}, {data.provincia}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Cotos relacionados</span>
                <div className="detail-value" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {data.cotos.map((coto, index) => (
                    <Tag key={index} variant="success">{coto}</Tag>
                  ))}
                </div>
              </div>
              <div className="detail-row">
                <span className="detail-label">Activo desde</span>
                <span className="detail-value">{data.activoDesde}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Tiempo activo</span>
                <span className="detail-value">{data.tiempoActivo}</span>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="card-content">
            <div className="logs-section-header">
              <h3 className="logs-section-title">Logs</h3>
              <div className="logs-section-controls">
                <div className="logs-filters-group">
                  <TextField
                    placeholder="Buscar..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    withIcon={true}
                    className="logs-search-input"
                  />
                  <Button variant="primary" className="logs-filter-button" size="s">Filtrar</Button>
                </div>
                <Button variant="accent" className="logs-download-button" size="s" onClick={() => setIsDownloadModalOpen(true)}>
                  <img src={iconDownload} alt="" className="button-icon button-icon-white" />
                  <span>Descargar</span>
                </Button>
              </div>
            </div>
            <div className="table-container-detalle logs-table-container">
              <div className="logs-table-wrapper">
                <div className="logs-table-content">
                  <div className="logs-table-header">
                    <div className="logs-header-left">
                      <div className="logs-header-text">Fecha y Hora</div>
                    </div>
                    <div className="logs-header-right">
                      <div className="logs-header-text">Consulta</div>
                    </div>
                  </div>
                  <div className="logs-table-body">
                    <div className="logs-timeline-column">
                      {data.logs.map((row, index) => (
                        <div key={index} className="logs-timeline-item">
                          <img src={logsIcon} alt="" className="logs-timeline-icon" />
                        </div>
                      ))}
                    </div>
                    <div className="logs-data-column">
                      {data.logs.map((row, index) => (
                        <div key={index} className={`logs-data-row ${index === data.logs.length - 1 ? 'logs-data-row-last' : ''}`}>
                          <div className="logs-data-left">
                            <div className="logs-date-time-container">
                              <div className="logs-date">{row.fecha}</div>
                              <div className="logs-time">{row.hora}</div>
                            </div>
                          </div>
                          <div className="logs-data-right">
                            <div className="logs-consulta">{row.consulta}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="logs-table-pagination">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={4}
                    onPageChange={setCurrentPage}
                  />
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <Modal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        onApply={() => {
          // Aquí iría la lógica para descargar el archivo
          setIsDownloadModalOpen(false)
        }}
        title="Descargar"
      >
        <DownloadSettings
          fileType={fileType}
          onFileTypeChange={setFileType}
        />
      </Modal>
    </div>
  )
}
